import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Star, Sparkles, Crown, Palette } from 'lucide-react';
import { DjayAvatar, DjayVariant, DjayAccessory, DjayTier, MascotRarity } from '../../mascot';
import { VariantUnlockInfo } from '../../mascot/useVariantUnlocks';
import { AccessoryUnlockInfo, TierUnlockInfo, CATEGORY_LABELS, AccessoryCategory } from '../../mascot/useDjayCustomization';
import { RADIUS } from '../../config/designTokens';
type CustomizationTab = 'looks' | 'accessories' | 'tiers';
interface DjayShowcaseProps {
  variants: VariantUnlockInfo[];
  selectedVariant: DjayVariant;
  onSelectVariant: (variantId: DjayVariant) => void;
  accessories: AccessoryUnlockInfo[];
  selectedAccessory: DjayAccessory;
  onSelectAccessory: (accessoryId: DjayAccessory) => void;
  tiers: TierUnlockInfo[];
  selectedTier: DjayTier;
  onSelectTier: (tierId: DjayTier) => void;
  focusedVariant: string | null;
  rarityStyles: Record<MascotRarity, {
    border: string;
    bg: string;
    text: string;
    glow: string;
    label: string;
  }>;
}
export function DjayShowcase({
  variants,
  selectedVariant,
  onSelectVariant,
  accessories,
  selectedAccessory,
  onSelectAccessory,
  tiers,
  selectedTier,
  onSelectTier,
  focusedVariant,
  rarityStyles
}: DjayShowcaseProps) {
  const [activeTab, setActiveTab] = useState<CustomizationTab>('looks');
  const activeVariantInfo = variants.find(v => v.id === selectedVariant);
  const activeAccessoryInfo = accessories.find(a => a.id === selectedAccessory);
  const activeTierInfo = tiers.find(t => t.id === selectedTier);
  return <div className={`bg-white p-6 sm:p-8 ${RADIUS.card} border-2 border-slate-100 shadow-sm flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start`} data-id="element-1181">
      {/* Left: Hero Djay */}
      <div className="flex flex-col items-center gap-4 w-full lg:w-1/3" data-id="element-1182">
        <div className="relative w-48 h-48 sm:w-56 sm:h-56 flex-shrink-0 flex items-center justify-center bg-gradient-to-b from-orange-50 to-amber-50 rounded-full border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-id="element-1183">
          <motion.div key={`${selectedVariant}-${selectedAccessory}-${selectedTier}`} initial={{
          scale: 0.8,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          bounce: 0.5,
          duration: 0.6
        }} className="transform scale-[1.8] mt-8" data-id="element-1184">
            <DjayAvatar size="xl" emotion="excited" variant={selectedVariant} accessory={selectedAccessory} tier={selectedTier} animate={true} data-id="element-1185" />
          </motion.div>
        </div>
        <div className="text-center" data-id="element-1186">
          <h3 className="text-xl font-black text-slate-800" data-id="element-1187">
            {activeVariantInfo?.name || 'Jouw Djay'}
          </h3>
          <div className="flex flex-col items-center gap-1 mt-1" data-id="element-1188">
            {activeAccessoryInfo && activeAccessoryInfo.id !== 'none' && <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-1189">
                + {activeAccessoryInfo.name}
              </span>}
            {activeTierInfo && activeTierInfo.id !== 'rookie' && <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1190">
                <Crown className="w-3 h-3" data-id="element-1191" /> {activeTierInfo.name}
              </span>}
          </div>
        </div>
      </div>

      {/* Right: Customizer */}
      <div className="flex-1 w-full flex flex-col gap-4" data-id="element-1192">
        {/* Tabs */}
        <div className="flex p-1 bg-slate-100 rounded-xl" data-id="element-1193">
          <button onClick={() => setActiveTab('looks')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'looks' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1194">
            <Palette className="w-4 h-4" data-id="element-1195" />
            Looks
          </button>
          <button onClick={() => setActiveTab('accessories')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'accessories' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1196">
            <Sparkles className="w-4 h-4" data-id="element-1197" />
            Accessoires
          </button>
          <button onClick={() => setActiveTab('tiers')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'tiers' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1198">
            <Crown className="w-4 h-4" data-id="element-1199" />
            Rang
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-1200">
          {activeTab === 'looks' && <motion.div key="looks" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1201">
              <div className="flex items-center justify-between mb-2" data-id="element-1202">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1203">
                  <Palette className="w-4 h-4 text-amber-500" data-id="element-1204" />
                  Kies je look
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1205">
                  {variants.filter(v => v.unlocked).length} /{' '}
                  {variants.length} ontgrendeld
                </span>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 sm:gap-4" data-id="element-1206">
                {variants.map(card => {
              const isSelected = selectedVariant === card.id;
              const isFocused = focusedVariant === card.id;
              const styles = rarityStyles[card.rarity];
              return <motion.button key={card.id} id={`variant-${card.id}`} whileHover={card.unlocked && !isSelected ? {
                scale: 1.05
              } : {}} whileTap={card.unlocked && !isSelected ? {
                scale: 0.95
              } : {}} onClick={() => {
                if (card.unlocked && !isSelected) onSelectVariant(card.id);
              }} className={`
                        relative aspect-square rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all
                        ${card.unlocked ? `${styles.bg} ${styles.border} cursor-pointer` : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? 'ring-4 ring-orange-400 ring-offset-2 shadow-md' : ''}
                        ${isFocused ? 'ring-4 ring-amber-400 ring-offset-2 animate-pulse' : ''}
                      `} title={card.name} aria-label={card.unlocked ? `Kies ${card.name}` : `Vergrendeld: ${card.name}`} data-id="element-1207">
                      {!card.unlocked && <div className="absolute top-2 right-2 z-20" data-id="element-1208">
                          <div className="w-5 h-5 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1209">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1210" />
                          </div>
                        </div>}

                      <div className={`flex items-center justify-center ${!card.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1211">
                        {card.rarity === 'secret' && !card.unlocked ? <span className="text-2xl font-black text-slate-300 block" data-id="element-1212">
                            ???
                          </span> : <DjayAvatar size="xl" emotion={card.unlocked ? isSelected ? 'excited' : 'happy' : 'sleeping'} variant={card.id} animate={false} data-id="element-1213" />}
                      </div>

                      {card.unlocked && <div className={`absolute bottom-0 left-0 right-0 h-1.5 ${styles.bg.replace('bg-', 'bg-').replace('50', '300')}`} data-id="element-1214" />}
                    </motion.button>;
            })}
              </div>

              {/* Selected/Focused Variant Details */}
              <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1215">
                {variants.map(v => {
              const showDetails = focusedVariant ? v.id === focusedVariant : v.id === selectedVariant;
              if (!showDetails) return null;
              return <motion.div key={v.id} initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex flex-col gap-2" data-id="element-1216">
                      <div className="flex items-center gap-2" data-id="element-1217">
                        <span className="font-black text-slate-700" data-id="element-1218">
                          {v.name}
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rarityStyles[v.rarity].bg} ${rarityStyles[v.rarity].text}`} data-id="element-1219">
                          {rarityStyles[v.rarity].label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500" data-id="element-1220">{v.description}</p>

                      {!v.unlocked && v.progress.max > 0 && <div className="mt-2" data-id="element-1221">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1222">
                            <span data-id="element-1223">{v.condition}</span>
                            <span data-id="element-1224">
                              {v.progress.current} / {v.progress.max}
                            </span>
                          </div>
                          <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1225">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${v.progress.current / v.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1226" />
                          </div>
                        </div>}
                    </motion.div>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'accessories' && <motion.div key="accessories" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1227">
              <div className="flex items-center justify-between mb-2" data-id="element-1228">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1229">
                  <Sparkles className="w-4 h-4 text-amber-500" data-id="element-1230" />
                  Kies je accessoire
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1231">
                  {accessories.filter(a => a.unlocked).length} /{' '}
                  {accessories.length} ontgrendeld
                </span>
              </div>

              <div className="flex flex-col gap-8" data-id="element-1232">
                {/* Geen accessoire optie */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1233">
                  {accessories.filter(a => a.id === 'none').map(acc => {
                const isSelected = selectedAccessory === acc.id;
                return <motion.button key={acc.id} whileHover={!isSelected ? {
                  scale: 1.02
                } : {}} whileTap={!isSelected ? {
                  scale: 0.98
                } : {}} onClick={() => {
                  if (!isSelected) onSelectAccessory(acc.id);
                }} className={`
                          relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                          bg-white border-slate-200 cursor-pointer hover:border-amber-300
                          ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                        `} data-id="element-1234">
                          <div className="h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-slate-50" data-id="element-1235">
                            <span className="text-5xl" data-id="element-1236">🚫</span>
                          </div>
                          <span className="font-black text-slate-800 text-base mb-0.5" data-id="element-1237">
                            {acc.name}
                          </span>
                        </motion.button>;
              })}
                </div>

                {/* Groepeer per categorie */}
                {(Object.keys(CATEGORY_LABELS) as AccessoryCategory[]).map(category => {
              const categoryAccessories = accessories.filter(a => a.category === category);
              if (categoryAccessories.length === 0) return null;
              return <div key={category} className="space-y-3" data-id="element-1238">
                        <h5 className="font-bold text-slate-400 uppercase tracking-wider text-xs border-b-2 border-slate-100 pb-2" data-id="element-1239">
                          {CATEGORY_LABELS[category]}
                        </h5>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1240">
                          {categoryAccessories.map(acc => {
                    const isSelected = selectedAccessory === acc.id;
                    return <motion.button key={acc.id} whileHover={acc.unlocked && !isSelected ? {
                      scale: 1.02
                    } : {}} whileTap={acc.unlocked && !isSelected ? {
                      scale: 0.98
                    } : {}} onClick={() => {
                      if (acc.unlocked && !isSelected) onSelectAccessory(acc.id);
                    }} className={`
                                relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                                ${acc.unlocked ? 'bg-white border-slate-200 cursor-pointer hover:border-amber-300' : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                                ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                              `} data-id="element-1241">
                                {!acc.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1242">
                                    <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1243">
                                      <Lock className="w-3 h-3 text-slate-500" data-id="element-1244" />
                                    </div>
                                  </div>}

                                <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-3 rounded-xl bg-gradient-to-b from-orange-50/60 to-amber-50/60 ${!acc.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1245">
                                  <DjayAvatar size="hero" emotion={isSelected ? 'excited' : 'happy'} variant="default" accessory={acc.id} animate={false} data-id="element-1246" />
                                </div>

                                <span className="font-black text-slate-800 text-base mb-0.5" data-id="element-1247">
                                  {acc.name}
                                </span>
                                <span className="text-xs font-medium text-slate-500" data-id="element-1248">
                                  {acc.condition}
                                </span>

                                {!acc.unlocked && acc.progress.max > 0 && <div className="w-full mt-3" data-id="element-1249">
                                    <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1250">
                                      <span data-id="element-1251">Voortgang</span>
                                      <span data-id="element-1252">
                                        {acc.progress.current} /{' '}
                                        {acc.progress.max}
                                      </span>
                                    </div>
                                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1253">
                                      <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                            width: 0
                          }} animate={{
                            width: `${acc.progress.current / acc.progress.max * 100}%`
                          }} transition={{
                            duration: 0.5
                          }} data-id="element-1254" />
                                    </div>
                                  </div>}
                              </motion.button>;
                  })}
                        </div>
                      </div>;
            })}
              </div>
            </motion.div>}

          {activeTab === 'tiers' && <motion.div key="tiers" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-4" data-id="element-1255">
              <div className="flex items-center justify-between mb-2" data-id="element-1256">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1257">
                  <Crown className="w-4 h-4 text-amber-500" data-id="element-1258" />
                  Kies je rang
                </h4>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1259">
                  {tiers.filter(t => t.unlocked).length} / {tiers.length}{' '}
                  ontgrendeld
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1260">
                {tiers.map(tier => {
              const isSelected = selectedTier === tier.id;
              return <motion.button key={tier.id} whileHover={tier.unlocked && !isSelected ? {
                scale: 1.02
              } : {}} whileTap={tier.unlocked && !isSelected ? {
                scale: 0.98
              } : {}} onClick={() => {
                if (tier.unlocked && !isSelected) onSelectTier(tier.id);
              }} className={`
                        relative p-4 rounded-2xl border-2 flex flex-col items-center justify-center overflow-hidden transition-all text-center
                        ${tier.unlocked ? 'bg-white border-slate-200 cursor-pointer hover:border-amber-300' : 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed'}
                        ${isSelected ? 'ring-4 ring-amber-400 ring-offset-2 shadow-md border-amber-300' : ''}
                      `} data-id="element-1261">
                      {!tier.unlocked && <div className="absolute top-3 right-3 z-20" data-id="element-1262">
                          <div className="w-6 h-6 rounded-full bg-slate-200/80 backdrop-blur-sm flex items-center justify-center" data-id="element-1263">
                            <Lock className="w-3 h-3 text-slate-500" data-id="element-1264" />
                          </div>
                        </div>}

                      <div className={`h-32 sm:h-36 w-full flex items-center justify-center mb-4 rounded-xl bg-gradient-to-b from-orange-50/60 to-amber-50/60 overflow-hidden ${!tier.unlocked ? 'grayscale opacity-60' : ''}`} data-id="element-1265">
                        <DjayAvatar size="hero" emotion={tier.unlocked ? isSelected ? 'proud' : 'happy' : 'sleeping'} variant="default" tier={tier.id} animate={false} data-id="element-1266" />
                      </div>

                      <span className="font-black text-slate-800 text-lg mb-1" data-id="element-1267">
                        {tier.name}
                      </span>
                      <span className="text-xs font-medium text-slate-500" data-id="element-1268">
                        {tier.condition}
                      </span>

                      {!tier.unlocked && tier.progress.max > 0 && <div className="w-full mt-3" data-id="element-1269">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1270">
                            <span data-id="element-1271">Voortgang</span>
                            <span data-id="element-1272">
                              {tier.progress.current} / {tier.progress.max}
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1273">
                            <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${tier.progress.current / tier.progress.max * 100}%`
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-1274" />
                          </div>
                        </div>}
                    </motion.button>;
            })}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}